import React from 'react';
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { CloseIcon } from '@/components/icons';

interface SlideOverProps {
  /** Whether the panel is open. */
  open: boolean;
  /** Called when the panel should close. */
  onClose: () => void;
  /** Panel title. */
  title: string;
  /** Optional subtitle/description. */
  subtitle?: string;
  /** Content inside the panel. */
  children: React.ReactNode;
  /** Footer content (e.g. save/cancel buttons). */
  footer?: React.ReactNode;
}

/**
 * Slide-over panel from the right edge.
 * Built on Headless UI Dialog with enter/leave transitions.
 */
export const SlideOver: React.FC<SlideOverProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}) => {
  return (
    <Transition show={open}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="motion-reduce:transition-none fixed inset-0 bg-black/50 backdrop-blur-md" />
        </TransitionChild>

        <div className="fixed inset-0 flex justify-end">
          <TransitionChild
            enter="transition-transform duration-300 ease-out"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-transform duration-200 ease-in"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="motion-reduce:transition-none w-full max-w-md h-full bg-background border-l border-border flex flex-col">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-border">
                <div>
                  <DialogTitle className="text-xl font-bold text-foreground">
                    {title}
                  </DialogTitle>
                  {subtitle && (
                    <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>
                  )}
                </div>
                <button
                  aria-label="Close"
                  onClick={onClose}
                  className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto scrollbar-dark p-6">
                {children}
              </div>

              {/* Footer */}
              {footer && (
                <div className="p-6 border-t border-border">
                  {footer}
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
